import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { PlatformAdminAPI } from '@saraa/permissions/api/PlatformAdminAPI.js';
import { UserAPI } from '@saraa/permissions/api/UserAPI.js';
import type { SaraaUser } from '@saraa/permissions/db/SaraaUser.js';
import { html, nothing } from 'lit';
import { sectionLoader } from './ui.js';

/**
 * Whether a user administers the platform.
 *
 * Replaces the ERM role editor, which offered a dropdown of role documents that
 * no longer exist. That control was also a liar: assigning the org-admin role to
 * someone with no memberships did nothing, and removing it broke a user whose
 * membership still said they administered an organization. There is exactly one
 * thing to decide here now, and memberships decide the rest.
 *
 * Rendered only for a platform admin, and never on your own account — the server
 * refuses both cases, and a control that always fails is worse than none.
 */
const PlatformAdminToggle = stateful(({ userId }: { userId: string }) => {
  const [reloadToken, setReloadToken] = useState(0);
  const me = useObservable(() => currentUser<SaraaUser>(), []);

  /**
   * Read through `UserAPI`, not the generic `getByID`.
   *
   * The read gate narrows the generic `AdornisUser` path to the caller's own
   * document, so `getByID` on anybody else comes back empty — the toggle would
   * have shown every other user as not-an-admin, and demoting one would have been
   * impossible because the control always read `false`. `getById` is guarded by
   * `Users.view` against the target, which is the question actually being asked.
   */
  const targetQuery = useQuery(() => UserAPI.getById(userId)({ _id: 1, isPlatformAdmin: 1 }), [userId, reloadToken]);
  const target = targetQuery.value as SaraaUser | null | undefined;
  const isAdmin = target?.isPlatformAdmin === true;

  const setAdmin = (next: boolean) =>
    guardDialog(async () => await PlatformAdminAPI.set(userId, next)(), {
      hasToConfirm: true,
      confirmText: next
        ? 'Make this user a platform administrator? They will be able to see and change everything, in every organization.'
        : 'Remove platform administrator from this user?',
      loadingText: 'Saving…',
      success: () => setReloadToken(reloadToken + 1),
    });

  // Until the current user resolves, assume the restrictive branch.
  if (!me?._id) return nothing;

  /**
   * Only a platform admin may appoint one, so for anybody else there is nothing
   * here to show — not a disabled control, and not an explanation of a power they
   * do not have. An ORG_ADMIN reaches this page for their own organization's
   * members, and `resolveSetPlatformAdmin` would refuse them.
   */
  if (me.isPlatformAdmin !== true) return nothing;

  // Your own account: the server refuses this in both directions, so there is
  // nothing to offer. The reason is worth stating — this is where someone would
  // otherwise go looking for the control.
  if (me._id === userId) {
    return html`
      <div class="rounded-box border-base-300 text-base-content/70 flex gap-2 border border-dashed p-4 text-sm">
        <span class="icon-[material-symbols--lock-outline] text-base-content/40 shrink-0 text-lg"></span>
        <p>
          You cannot change your own platform administrator status. Removing it would leave you unable to grant it back.
          Ask another administrator.
        </p>
      </div>
    `;
  }

  if (targetQuery.loading) {
    return sectionLoader({ compact: true });
  }

  return html`
    <section class="flex flex-col gap-2">
      <p class="text-base-content text-sm font-semibold">Platform access</p>
      <label
        class="rounded-box border-base-300 hover:border-base-content/20 flex cursor-pointer items-start gap-3 border p-4"
      >
        <input
          type="checkbox"
          class="toggle toggle-primary mt-0.5 shrink-0"
          .checked=${isAdmin}
          @change=${() => void setAdmin(!isAdmin)}
        />
        <span class="flex flex-col gap-0.5">
          <span class="text-base-content text-sm font-medium">Platform administrator</span>
          <span class="text-base-content/60 text-xs">
            Full access to every organization, plus user and organization management. Most users should instead be given
            an organization membership above.
          </span>
        </span>
      </label>
    </section>
  `;
});

/** ERM detail section wrapper. */
export const PlatformAdminSection = {
  // biome-ignore lint/suspicious/noExplicitAny: the ERM section context is entity-specific
  render: ({ entity }: { entity?: any }) => (entity?._id ? PlatformAdminToggle({ userId: entity._id }) : nothing),
};
